import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Environment } from '../types/environment';

export default function ViewEnvironmentPage() {
    const { environmentID } = useParams<{ environmentID: string }>();
    const navigate = useNavigate();
    const [env, setEnv] = useState<Environment | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const fieldStyle = { width: '100%', padding: '6px' } as const;
    const labelStyle = { width: '100%', display: 'flex', flexDirection: 'column' } as const;

    useEffect(() => {
        async function load() {
            try {
                const resp = await fetch(`/api/environments/${environmentID}`);
                if (resp.ok) {
                    const data = await resp.json();
                    setEnv(data);
                } else {
                    const msg = await resp.text();
                    setErrors((e) => [...e, msg]);
                }
            } catch (err) {
                console.error('GetEnvironment error', err);
                setErrors((e) => [...e, 'GetEnvironment error']);
            }
        }
        load();
    }, [environmentID]);

    async function handleDelete() {
        if (!environmentID) return;
        if (!window.confirm('Are you sure you want to delete this environment?')) {
            return;
        }
        try {
            const resp = await fetch(`/api/environments/${environmentID}`, {
                method: 'DELETE',
            });
            if (resp.ok || resp.status === 204) {
                navigate('/environments');
            } else {
                const msg = await resp.text();
                setErrors((e) => [...e, msg]);
            }
        } catch (err) {
            console.error('DeleteEnvironment error', err);
            setErrors((e) => [...e, 'DeleteEnvironment error']);
        }
    }

    const mask = (v: string) => '*'.repeat(v.length);

    return (
        <div style={{ paddingTop: '60px', maxWidth: '800px', margin: '0 auto' }}>
            {errors.map((er, i) => (
                <div key={i} style={{ color: 'red', marginBottom: '8px' }}>
                    {er}
                </div>
            ))}
            {env && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button onClick={() => navigate(`/environments/${environmentID}/edit`)}>Edit</button>
                        <button onClick={handleDelete}>Delete</button>
                    </div>
                    <h1>View Environment</h1>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={labelStyle}>
                            Repositories
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {env.Repos.map((r, i) => {
                                    const repo = r.Repo.startsWith(`${r.Org}/`) ? r.Repo.slice(r.Org.length + 1) : r.Repo;
                                    return (
                                        <input key={i} value={`${r.Org}/${repo}`} disabled style={fieldStyle} />
                                    );
                                })}
                            </div>
                        </label>
                        <label style={labelStyle}>
                            Name
                            <input value={env.Name} disabled style={fieldStyle} />
                        </label>
                        <label style={labelStyle}>
                            Context
                            <textarea value={env.Context || ''} disabled style={{ ...fieldStyle, minHeight: '80px' }} />
                        </label>
                        <label style={labelStyle}>
                            Image
                            <input value={env.Image || ''} disabled style={fieldStyle} />
                        </label>
                        {env.EnvVars && env.EnvVars.length > 0 && (
                            <div>
                                <div>Environment variables</div>
                                {env.EnvVars.map((v, i) => (
                                    <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                        <input value={v.Key} disabled style={{ flex: 1, padding: '6px' }} />
                                        <input value={v.Value} disabled style={{ flex: 1, padding: '6px' }} />
                                    </div>
                                ))}
                            </div>
                        )}
                        {env.Secrets && env.Secrets.length > 0 && (
                            <div>
                                <div>Secrets</div>
                                {env.Secrets.map((s, i) => (
                                    <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                        <input value={s.Key} disabled style={{ flex: 1, padding: '6px' }} />
                                        <input
                                            type="password"
                                            value={mask(s.Value)}
                                            disabled
                                            style={{ flex: 1, padding: '6px' }}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        {env.BootstrapScript && (
                            <label style={labelStyle}>
                                Bootstrap script
                                <textarea
                                    value={env.BootstrapScript}
                                    disabled
                                    style={{ ...fieldStyle, minHeight: '80px' }}
                                />
                            </label>
                        )}
                        {env.AllowedEndpoints && env.AllowedEndpoints.length > 0 && (
                            <div>
                                <div>Allowed endpoints</div>
                                {env.AllowedEndpoints.map((ep, i) => (
                                    <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                        <input value={ep} disabled style={{ flex: 1, padding: '6px' }} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
